import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BOOK } from "@/lib/book";

const THEMES = [
  ["Attraction", "What draws two people together, and the everyday behaviors that strengthen or weaken that pull."],
  ["Compatibility", "How each person's priorities and current relational mode shape what they want from dating."],
  ["Reciprocity", "Why one person often ends up investing more, and how those roles can quietly reverse."],
  ["Dating expectations", "How your real or perceived options influence your dating experiences and decisions."],
  ["Balanced partnerships", "Practical ways to keep a relationship mutually beneficial rather than one-sided."],
];

const linkCls = "underline decoration-[#D92D20] underline-offset-4 hover:text-[#D92D20] transition-colors";

export default function AboutBook() {
  return (
    <section id="about-the-book" className="border-b border-black/10" data-testid="about-book-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28 grid grid-cols-12 gap-x-6 gap-y-14">
        <div className="col-span-12 lg:col-span-6">
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-4">The book at a glance</div>
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl leading-[0.95]" data-testid="about-book-heading">
            About {BOOK.title}
          </h2>
          <div className="mt-8 space-y-5 text-lg leading-relaxed">
            <p>
              <em className="not-italic font-medium">{BOOK.title}: {BOOK.subtitle}</em> is a {BOOK.pages}-page paperback by relationship coach, author, and podcaster {BOOK.author}, published by {BOOK.publisher} in May 2025.
            </p>
            <p className="text-[#525252]">
              The book examines the patterns behind modern dating and organizes them into twelve laws you can name and recognize. Its focus is balance: understanding how power and investment shift between two people, so a connection doesn't drift into a one-sided relationship.
            </p>
            <p>
              It is written for both men and women, whether you're single and dating, in a relationship, or trying to make sense of past ones. If you've felt like the one who cares more, or watched connections start strong and then fade, you'll find a clear framework for what's happening.
            </p>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 lg:col-start-8">
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl leading-[0.95]" data-testid="discover-heading">
            What You'll Discover in the Book
          </h2>
          <dl className="mt-8 divide-y divide-black/10 border-y border-black/10">
            {THEMES.map(([term, desc]) => (
              <div key={term} className="py-4" data-testid={`discover-theme-${term.toLowerCase().replace(/\s+/g, "-")}`}>
                <dt className="font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[#D92D20]">{term}</dt>
                <dd className="mt-1 text-[#525252] leading-relaxed">{desc}</dd>
              </div>
            ))}
          </dl>
          <ul className="mt-8 space-y-3 text-sm">
            <li><a href="#laws" className={linkCls} data-testid="about-link-laws">Explore all 12 laws of dating dynamics</a></li>
            <li><a href="#author" className={linkCls} data-testid="about-link-author">Read about author Manuel Santana</a></li>
            <li>
              <Link to="/checkout" className="inline-flex items-center gap-2 font-medium hover:text-[#D92D20] transition-colors" data-testid="about-link-checkout">
                Order a signed paperback of {BOOK.title} <ArrowRight className="w-4 h-4" />
              </Link>
            </li>
          </ul>
          <p className="mt-6 text-sm text-[#525252]">
            Already ordered? <Link to="/track" className={linkCls} data-testid="about-link-track">Track your book order</Link> or{" "}
            <Link to="/contact" className={linkCls} data-testid="about-link-contact">contact our support team</Link>.
          </p>
        </div>
      </div>
    </section>
  );
}
